import { CheckIcon, ArrowForwardIcon } from '@chakra-ui/icons';
import { Box, Flex, VStack, HStack, Text, Icon, Image, useToast } from '@chakra-ui/react';
import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { MdError } from 'react-icons/md';
import { AccountContext } from '../../AccountContext';
import Desconto from '../../../images/Desconto.svg';
import LogoDeoVita from '../../../images/petalas-deovita.svg';
import { api } from 'api/api';

const ENDPOINT =
  process.env.REACT_APP_ENVIRONMENT === 'production'
    ? process.env.REACT_APP_BASE_URL_API_SERVER_PROD
    : process.env.REACT_APP_BASE_URL_API_SERVER_TEST;

const REACT_APP_DEOVITA_RECIPIENT_ID =
  process.env.REACT_APP_ENVIRONMENT === 'production'
    ? process.env.REACT_APP_DEOVITA_RECIPIENT_ID_PROD
    : process.env.REACT_APP_DEOVITA_RECIPIENT_ID_TEST;

const COLORS = {
  teal: '#20A39E',
  bg: '#F4F6F8',
  heading: '#1A2B2A',
  subtext: '#6B7B7A',
  mutedText: '#9AA8A7',
  border: '#E4E9E9',
  noticeBg: '#FDECEA',
  noticeBorder: '#F5C6C0',
  noticeIcon: '#C0392B',
  noticeText: '#8E2A20',
  badgeOverdueBg: '#FDECEA',
  badgeOverdueDot: '#C0392B',
  badgeOverdueText: '#C0392B',
  badgeUpcomingBg: '#FEF2DE',
  badgeUpcomingDot: '#F5A623',
  badgeUpcomingText: '#B26A00',
  badgePaidBg: '#E6F4EA',
  badgePaidDot: '#2E7D32',
  badgePaidText: '#2E7D32',
  badgePendingBg: '#E8EEF7',
  badgePendingDot: '#3B6FB0',
  badgePendingText: '#2F5590',
};

function capitalizeFirstLetter(str) {
  return str ? str.charAt(0).toUpperCase() + str.slice(1) : str;
}

function formatCurrency(value) {
  return (Number(value) || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function getBadgeInfo(parcela) {
  if (parcela.status === 'paid') {
    return { label: 'Paga', bg: COLORS.badgePaidBg, dot: COLORS.badgePaidDot, text: COLORS.badgePaidText };
  }
  if (parcela.status === 'pending') {
    return { label: 'Processando', bg: COLORS.badgePendingBg, dot: COLORS.badgePendingDot, text: COLORS.badgePendingText };
  }
  const dataParcela = parcela.data ? new Date(parcela.data.replace(' ', 'T')) : null;
  const mes = dataParcela ? capitalizeFirstLetter(dataParcela.toLocaleDateString('pt-BR', { month: 'long' })) : null;
  const overdue = dataParcela ? dataParcela < new Date() : false;

  if (overdue) {
    return { label: mes ? `Venceu em ${mes}` : 'Em atraso', bg: COLORS.badgeOverdueBg, dot: COLORS.badgeOverdueDot, text: COLORS.badgeOverdueText };
  }
  return { label: mes ? `Vence em ${mes}` : 'Em aberto', bg: COLORS.badgeUpcomingBg, dot: COLORS.badgeUpcomingDot, text: COLORS.badgeUpcomingText };
}

function CheckBox({ checked, disabled, onClick, size = 24, radius = 7 }) {
  return (
    <Flex
      as="button"
      type="button"
      onClick={disabled ? undefined : onClick}
      align="center"
      justify="center"
      boxSize={`${size}px`}
      minW={`${size}px`}
      borderRadius={`${radius}px`}
      bg={checked ? COLORS.teal : 'white'}
      border={checked ? 'none' : `1.5px solid ${COLORS.border}`}
      cursor={disabled ? 'default' : 'pointer'}
      opacity={disabled ? 0.6 : 1}
      flexShrink={0}
    >
      {checked && <CheckIcon color="white" boxSize={size >= 24 ? '10px' : '9px'} />}
    </Flex>
  );
}

function StatusBadge({ info }) {
  return (
    <HStack spacing="5px" bg={info.bg} borderRadius="8px" px="9px" py="4px" alignSelf="flex-start">
      <Box boxSize="6px" borderRadius="full" bg={info.dot} flexShrink={0} />
      <Text fontSize="11px" fontWeight="600" color={info.text} whiteSpace="nowrap">
        {info.label}
      </Text>
    </HStack>
  );
}

function ParcelaCard({ parcela, checked, disabled, onToggle, descricaoPlano }) {
  const badge = getBadgeInfo(parcela);
  const dataParcela = parcela.data ? new Date(parcela.data.replace(' ', 'T')) : null;
  const mes = dataParcela ? capitalizeFirstLetter(dataParcela.toLocaleDateString('pt-BR', { month: 'long' })) : null;
  const planoDisplay = descricaoPlano ? capitalizeFirstLetter(String(descricaoPlano).toLowerCase()) : 'Individual';
  const subtitle = mes ? `Plano ${planoDisplay} · ${mes}` : `Plano ${planoDisplay}`;

  return (
    <Flex bg="white" borderRadius="16px" border={`1px solid ${COLORS.border}`} p="16px" gap="14px" align="flex-start" w="100%">
      <Box mt="2px">
        <CheckBox checked={checked} disabled={disabled} onClick={onToggle} />
      </Box>
      <VStack align="start" spacing="8px" flex={1} minW={0}>
        <StatusBadge info={badge} />
        <Text fontSize="15px" fontWeight="600" color={COLORS.heading}>
          Assinatura Mensal
        </Text>
        <Text fontSize="13px" color={COLORS.subtext}>
          {subtitle}
        </Text>
      </VStack>
      <VStack align="end" spacing="3px" flexShrink={0}>
        <Text fontSize="11px" fontWeight="600" color={COLORS.mutedText}>
          R$
        </Text>
        <Text fontSize="18px" fontWeight="700" color={COLORS.heading}>
          {formatCurrency(parcela.valor)}
        </Text>
      </VStack>
    </Flex>
  );
}

function ListaParcelas(props) {
  const navigate = useNavigate();
  const { user, page, setPage, setUser } = useContext(AccountContext);
  useEffect(() => {
    setPage('Parcelas');
  }, []);

  const [selecionados, setSelecionados] = useState([]);
  const [valor_selecionado, setValorSelecionado] = useState(0);
  const [valor_desconto, setValorDesconto] = useState(0);
  const status = props?.status;
  const [parcelas, setParcelas] = useState(props.parcelas ? props.parcelas : []);

  async function fetchStatusParcelas() {
    try {
      const recipientsIds = parcelas.filter((parcela) => parcela.id_transaction).map((parcela) => parcela.id_transaction);

      const response = await api(`${ENDPOINT}/clients/fetchStatusParcelas`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ parcelas: recipientsIds }),
      });

      if (!response.ok) {
        console.log('Erro na resposta: ', response);
        return;
      }
      const responseData = await response.json();
      const updatedParcelas = parcelas.map((parcela) => {
        const matchingResponse = responseData.find((item) => item.id === parcela.id_transaction);
        return matchingResponse ? { ...parcela, status: matchingResponse.status } : parcela;
      });

      const filteredParcelas = updatedParcelas.filter(
        (parcela) => (parcela.status !== 'failed' || parcela.status !== 'paid') && parcela.id_transaction
      );

      setParcelas(updatedParcelas);
      setUser((prevState) => ({
        ...prevState,
        pesquisaParcelasPendentes: filteredParcelas,
      }));
    } catch (error) {
      console.error('Erro ao buscar status das parcelas: ', error);
    }
  }

  useEffect(() => {
    fetchStatusParcelas();
  }, []);

  const url = `${ENDPOINT}/client/parcelas`;
  const { id_contrato, descricao_plano, titular } = user;
  const [todosMarcados, setTodosMarcados] = useState(false);

  const checkboxValues = (object) => {
    const index = selecionados.findIndex((item) => item.parcela_id === object.parcela_id);
    if (index !== -1) {
      setSelecionados(selecionados.filter((item) => item.parcela_id !== object.parcela_id));
    } else {
      setSelecionados([...selecionados, object]);
    }
  };

  const toast = useToast({
    title: 'Aviso',
    description: 'Selecione no mínimo uma parcela',
    status: 'info',
    duration: 2000,
    position: 'bottom',
    colorScheme: 'red',
    containerStyle: {
      paddingTop: '50px',
    },
  });

  const enviarPagamento = () => {
    let split = [];
    if (user?.clienteImportado?.clientImportado === true || user?.clienteImportado?.clientImportado === 'true') {
      const parcelaComTaxaAdesao = parcelas ? parcelas?.find((parcela) => parcela.taxa_adesao === true) : null;
      const vendedorAmount = parcelaComTaxaAdesao?.split_vendedor || 0;
      const deovitaAmount = 100 - vendedorAmount;
      split = [
        {
          // Vendedor
          amount: vendedorAmount ? Math.round(vendedorAmount) : vendedorAmount,
          recipient_id: `${user?.clienteImportado?.empreendedor?.recipient_id_pagarme}`,
          type: 'percentage',
          options: {
            charge_processing_fee: false,
            charge_remainder_fee: false,
            liable: false,
          },
        },
        {
          // Deovita
          amount: deovitaAmount,
          recipient_id: REACT_APP_DEOVITA_RECIPIENT_ID,
          type: 'percentage',
          options: {
            charge_processing_fee: true,
            charge_remainder_fee: true,
            liable: true,
          },
        },
      ];
    }

    if (valor_selecionado >= 1.0) {
      const data = {
        paciente_id: user.id_cliente_deovita,
        username: user.name,
        parcelsActive: true,
        paciente_contrato_id: user.titular ? user.id_contrato : user.id_contrato_dependent.paciente_contrato_id,
        valor:
          valor_selecionado >= 1.0 && valor_desconto >= 1.0 && status === 'cancelamento_contrato' && user.id_forma_pagamento !== 13
            ? valor_desconto
            : valor_selecionado,
        valor_desconto: valor_desconto,
        status: status,
        expiration_pix_in_min: 5,
        navig_to: status === 'cancelamento_contrato' ? '/perfil/assinaturas/contrato-cancelado-sucesso' : '/perfil/dependentes/processar-pagamento',
        item: {
          paciente_id: user.id_cliente_deovita,
          descricao: 'Pagamento parcelas em atraso',
          paciente_contrato_parcelas_id: selecionados.map((item) => ({
            parcela_id: item.parcela_id,
            valor: item.valor,
            valor_desconto: item.valor_desconto,
          })),
          paciente_contrato_id: user.titular ? user.id_contrato : user.id_contrato_dependent.paciente_contrato_id,
          valor: valor_selecionado,
          valor_desconto: valor_desconto,
          status: status,
          whatsapp: user.phone1 || user.phone2,
          nome: user.name,
          valor_pendente: user?.qntdeParcelas?.valorParcelas,
          motivo: status === 'cancelamento_contrato' ? props?.motivo : '',
          desconto:
            valor_selecionado >= 1.0 && valor_desconto >= 1.0 && status === 'cancelamento_contrato' && user.id_forma_pagamento !== 13 ? true : false,
          pagamentoAdesao: user?.clienteImportado?.clientImportado === true || user?.clienteImportado?.clientImportado === 'true' ? true : false,
          dadosClienteImportado:
            user?.clienteImportado?.clientImportado === true || user?.clienteImportado?.clientImportado === 'true' ? user?.clienteImportado : null,
          split: split ? [...split] : null,
          split_rules: split ? [...split] : null,
        },
        orientacoes: '',
        service: 'settle_the_debt',
        confim: true,
        arrayPagamento: selecionados,
        split: split,
        frequenciaText: 'Pagamento de dívida',
      };
      navigate('/pagamentos', { state: data });
    } else {
      toast();
    }
  };

  useEffect(() => {
    if (todosMarcados) {
      setSelecionados(
        parcelas
          .filter((parcela) => parcela.status !== 'pending' && parcela.status !== 'paid')
          .map((parcela) => ({
            parcela_id: parcela.paciente_contrato_parcelas_id,
            valor: parcela.valor,
            valor_desconto: parcela.valor * 0.5,
          }))
      );
      return;
    }

    setSelecionados([]);
  }, [todosMarcados]);

  useEffect(() => {
    let valorSomado = 0;
    selecionados.forEach((selecionado) => {
      const valorFloat = parseFloat(selecionado.valor);
      if (!isNaN(valorFloat)) {
        valorSomado += valorFloat;
      }
    });
    setValorSelecionado(valorSomado);

    const clienteSelecionouMetadeOuMais = selecionados?.length >= parcelas.length / 2;

    if (!clienteSelecionouMetadeOuMais) {
      setValorDesconto(0);
    } else {
      const valorComDesconto = valorSomado * 0.5;
      setValorDesconto(valorComDesconto);
    }
  }, [selecionados, parcelas]);

  const handleMasterCheckboxChange = () => {
    setTodosMarcados(!todosMarcados);
    if (!todosMarcados) {
      setSelecionados(
        parcelas
          .filter((parcela) => parcela.status !== 'pending' && parcela.status !== 'paid')
          .map((parcela) => ({
            parcela_id: parcela.paciente_contrato_parcelas_id,
            valor: parcela.valor,
            valor_desconto: parcela.valor * 0.5,
          }))
      );
    } else {
      setSelecionados([]);
    }
  };

  async function fetchMensalidades() {
    toast.closeAll();
    const toastId = toast({
      title: 'Carregando',
      description: 'Aguarde... Estamos carregando as informações.',
      status: 'warning',
      isClosable: true,
      position: 'bottom',
      colorScheme: 'red',
      duration: null,
      type: 'loading',
      containerStyle: {
        paddingTop: '50px',
      },
    });

    const response = await api(url, {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      method: 'POST',
      body: JSON.stringify({
        id_contrato: user?.clienteImportado?.clientImportado ? user?.clienteImportado?.empreendedor?.contrato_id : user.id_contrato,
        titular: user.titular,
        id_contrato_dependente: user.id_contrato_dependent,
        adesaoImportada: user?.clienteImportado?.clientImportado,
      }),
    });
    await response.json().then(async (postsData) => {
      toast.close(toastId);
      if (postsData?.length === 0) {
        navigate('/dashboard/financeiro/sucesso-pagamento');
      } else {
        if (valor_selecionado >= '1.00') {
          enviarPagamento();
        } else {
          toast();
        }
      }
    });
  }

  const showDiscount = valor_selecionado >= 1.0 && valor_desconto >= 1.0 && status === 'cancelamento_contrato' && user.id_forma_pagamento !== 13;
  const totalExibido = showDiscount ? valor_desconto : valor_selecionado;

  const planoLabel =
    user.situacao_contrato === 'P'
      ? 'Contrato em análise'
      : titular === false
      ? 'Assinante · Dependente'
      : id_contrato && descricao_plano
      ? `Assinante · Plano ${capitalizeFirstLetter(String(descricao_plano).toLowerCase())}`
      : 'Assinante · Plano Individual';

  return (
    <Box bg={COLORS.bg} minH="100vh" w="100%" pb="140px">
      <VStack align="stretch" spacing="16px" px="20px" py="24px">
        <HStack bg="white" border={`1px solid ${COLORS.border}`} borderRadius="14px" p="14px" spacing="12px" align="center">
          <Flex align="center" justify="center" boxSize="40px" borderRadius="10px" bg="#5AB2AE" flexShrink={0}>
            <Image src={LogoDeoVita} boxSize="24px" objectFit="contain" alt="DeoVita" />
          </Flex>
          <VStack align="start" spacing="2px" flex={1} minW={0}>
            <Text color={COLORS.subtext} fontSize="12px">
              Seu plano DeoVita
            </Text>
            <Text color={COLORS.heading} fontSize="14px" fontWeight="600">
              {planoLabel}
            </Text>
          </VStack>
        </HStack>

        <VStack align="start" spacing="6px">
          <Text fontSize="20px" fontWeight="700" color={COLORS.heading}>
            Selecione as parcelas a pagar
          </Text>
          <Text fontSize="14px" color={COLORS.subtext}>
            Você possui {parcelas.length} {parcelas.length === 1 ? 'parcela pendente' : 'parcelas pendentes'} no seu plano.
          </Text>
        </VStack>

        <HStack bg={COLORS.noticeBg} border={`1px solid ${COLORS.noticeBorder}`} borderRadius="12px" p="12px" spacing="10px" align="flex-start">
          <Icon as={MdError} color={COLORS.noticeIcon} boxSize="20px" mt="1px" flexShrink={0} />
          <Text fontSize="12px" fontWeight="500" color={COLORS.noticeText}>
            Regularize suas parcelas para manter seu plano ativo.
          </Text>
        </HStack>

        <Flex justify="space-between" align="center">
          <HStack spacing="8px">
            <CheckBox checked={todosMarcados} onClick={handleMasterCheckboxChange} size={22} radius={6} />
            <Text fontSize="14px" fontWeight="600" color={COLORS.heading}>
              Selecionar todas
            </Text>
          </HStack>
          <Text fontSize="13px" color={COLORS.mutedText}>
            {parcelas.length} {parcelas.length === 1 ? 'parcela' : 'parcelas'}
          </Text>
        </Flex>

        <VStack align="stretch" spacing="12px" maxH="420px" overflowY="auto" pr="4px">
          {parcelas.map((parcela, index) => (
            <ParcelaCard
              key={parcela.paciente_contrato_parcelas_id || index}
              parcela={parcela}
              descricaoPlano={descricao_plano}
              disabled={parcela.status === 'pending' || parcela.status === 'paid'}
              checked={
                parcela.status === 'paid' || selecionados.some((item) => item.parcela_id === parcela.paciente_contrato_parcelas_id)
              }
              onToggle={() => checkboxValues({ parcela_id: parcela.paciente_contrato_parcelas_id, valor: parcela.valor })}
            />
          ))}
        </VStack>

        {status === 'cancelamento_contrato' && user.id_forma_pagamento !== 13 && (
          <HStack bg={COLORS.teal} borderRadius="14px" p="14px" spacing="12px" align="flex-start">
            <Image boxSize="20px" objectFit="cover" src={Desconto} />
            <VStack align="start" spacing="4px">
              <Text color="white" fontWeight="700" fontSize="15px">
                Tenha 50% de desconto!
              </Text>
              <Text color="white" fontSize="13px">
                Selecionando metade dos pagamentos pendentes, você adquire 50% de desconto no pagamento de suas parcelas pendentes!
              </Text>
            </VStack>
          </HStack>
        )}
      </VStack>

      {valor_selecionado >= 1.0 && (
        <Box bg="white" borderTop={`1px solid ${COLORS.border}`} px="20px" py="18px">
          <Flex justify="space-between" align="center" mb="14px">
            <VStack align="start" spacing="2px">
              <Text fontSize="13px" color={COLORS.subtext}>
                Total selecionado
              </Text>
              <Text fontSize="12px" color={COLORS.mutedText}>
                {selecionados.length} {selecionados.length === 1 ? 'parcela selecionada' : 'parcelas selecionadas'}
              </Text>
            </VStack>
            <VStack align="end" spacing="0">
              <Text fontSize="22px" fontWeight="700" color={COLORS.heading}>
                R$ {formatCurrency(totalExibido)}
              </Text>
              {showDiscount && (
                <Text fontSize="12px" fontWeight="600" color={COLORS.badgeUpcomingText}>
                  50% de desconto aplicado
                </Text>
              )}
            </VStack>
          </Flex>
          <Flex
            as="button"
            type="button"
            onClick={fetchMensalidades}
            bg={COLORS.teal}
            borderRadius="14px"
            h="54px"
            align="center"
            justify="center"
            gap="8px"
            w="100%"
          >
            <Text color="white" fontWeight="700" fontSize="16px">
              Pagar parcelas
            </Text>
            <ArrowForwardIcon color="white" boxSize="20px" />
          </Flex>
        </Box>
      )}
    </Box>
  );
}

export default ListaParcelas;
